/**
 * EditableField — inline click-to-edit for a single solicitation field.
 * Modes:
 *   - text        : single-line input
 *   - textarea    : multi-line input
 *   - number      : number input (formatted for display as $ if isMoney)
 *   - date        : date input
 *   - datetime    : datetime-local input
 *   - select      : dropdown (options required)
 *   - user        : user dropdown
 *
 * UX: displays the value read-only. Click to edit. On blur or Enter, saves
 * via onSave. Escape cancels. Optimistic-render with a small "Saving…" tag.
 */
import React, { useState, useRef, useEffect } from 'react';

type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'datetime' | 'select' | 'user' | 'email' | 'phone';

interface Props {
  label: string;
  value: any;
  type?: FieldType;
  options?: Array<{ value: string; label: string }>;
  users?: Array<{ id: string; name?: string; email?: string }>;
  placeholder?: string;
  isMoney?: boolean;           // format as $ on display
  fromSam?: boolean;           // show 📡 indicator
  onSave: (val: any) => Promise<void> | void;
  displayFormat?: (v: any) => string;
  emptyText?: string;
  compact?: boolean;
}

const S: Record<string, React.CSSProperties> = {
  wrap: { marginBottom: 14, position: 'relative' as const },
  label: {
    fontSize: 10, color: '#8fa8c0', textTransform: 'uppercase' as const,
    letterSpacing: 0.6, marginBottom: 4, fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  display: {
    fontSize: 13, color: '#e5edf5', lineHeight: 1.4, wordBreak: 'break-word' as const,
    minHeight: 20, padding: '4px 6px', borderRadius: 4,
    cursor: 'text', transition: 'background 0.1s',
  },
  displayEmpty: { color: '#526980', fontStyle: 'italic' as const },
  displayHover: { background: '#243447' },
  input: {
    background: '#243447', border: '1px solid #4a7eb5', borderRadius: 6,
    padding: '6px 10px', color: '#e5edf5', fontSize: 13, width: '100%',
    boxSizing: 'border-box' as const, outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    background: '#243447', border: '1px solid #4a7eb5', borderRadius: 6,
    padding: '6px 10px', color: '#e5edf5', fontSize: 13, width: '100%',
    boxSizing: 'border-box' as const, outline: 'none',
    minHeight: 60, resize: 'vertical' as const, fontFamily: 'inherit',
  },
  chipSam: {
    padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 700,
    background: '#4a7eb555', color: '#a3c5e5',
  },
  saving: {
    fontSize: 10, color: '#eab308', fontStyle: 'italic' as const,
  },
  saved: {
    fontSize: 10, color: '#10b981',
  },
};

const EditableField: React.FC<Props> = ({
  label, value, type = 'text', options, users, placeholder,
  isMoney, fromSam, onSave, displayFormat, emptyText = '—', compact,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(value ?? '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus?.();
  }, [editing]);

  const commit = async () => {
    if (!editing) return;
    const submitted = draft === '' ? null : draft;
    if (submitted === (value ?? null)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(submitted);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1200);
    } catch (e) {
      // revert
      setDraft(value ?? '');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => { setDraft(value ?? ''); setEditing(false); };

  const displayValue = (() => {
    if (value === null || value === undefined || value === '') return null;
    if (displayFormat) return displayFormat(value);
    if (isMoney && typeof value === 'number') return `$${value.toLocaleString()}`;
    if (type === 'select' && options) return options.find((o) => o.value === value)?.label || String(value);
    if (type === 'user' && users) {
      const u = users.find((x) => x.id === value);
      return u?.name || u?.email || String(value);
    }
    if (type === 'date') {
      try { return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
      catch { return String(value); }
    }
    if (type === 'datetime') {
      try { return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
      catch { return String(value); }
    }
    return String(value);
  })();

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          ref={inputRef}
          style={S.textarea}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commit();
          }}
        />
      );
    }
    if (type === 'select') {
      return (
        <select
          ref={inputRef}
          style={S.input}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); }}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
        >
          <option value="">— none —</option>
          {options?.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
      );
    }
    if (type === 'user') {
      return (
        <select
          ref={inputRef}
          style={S.input}
          value={draft || ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
        >
          <option value="">— Unassigned —</option>
          {users?.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
        </select>
      );
    }
    const inputType = type === 'datetime' ? 'datetime-local'
      : type === 'date' ? 'date'
      : type === 'number' ? 'number'
      : type === 'email' ? 'email'
      : type === 'phone' ? 'tel'
      : 'text';
    let inputValue = draft;
    if (type === 'date' && draft) inputValue = String(draft).slice(0, 10);
    if (type === 'datetime' && draft) inputValue = String(draft).slice(0, 16);
    return (
      <input
        ref={inputRef}
        style={S.input}
        type={inputType}
        value={inputValue}
        placeholder={placeholder}
        onChange={(e) => {
          if (type === 'number') {
            setDraft(e.target.value === '' ? '' : Number(e.target.value));
          } else {
            setDraft(e.target.value);
          }
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel();
          if (e.key === 'Enter') commit();
        }}
      />
    );
  };

  return (
    <div style={{ ...S.wrap, marginBottom: compact ? 8 : 14 }}>
      <div style={S.label}>
        <span>{label}</span>
        {fromSam && <span style={S.chipSam}>📡 SAM</span>}
        {saving && <span style={S.saving}>Saving…</span>}
        {justSaved && <span style={S.saved}>✓ Saved</span>}
      </div>
      {editing ? (
        renderInput()
      ) : (
        <div
          style={{
            ...S.display,
            ...(displayValue == null ? S.displayEmpty : {}),
            ...(hover ? S.displayHover : {}),
          }}
          onClick={() => setEditing(true)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title="Click to edit"
        >
          {displayValue == null ? emptyText : displayValue}
        </div>
      )}
    </div>
  );
};

export default EditableField;
